import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { useAPIClient } from '@/hooks/useAPIClient';
import { 
  InputFilesListResponse, 
  RawFileViewResponse, 
  ProcessedFileResponse, 
  FileMappingResponse 
} from '@/types/api';

export function useInputFiles(module?: string): UseQueryResult<InputFilesListResponse, Error> {
  const apiClient = useAPIClient();

  return useQuery({
    queryKey: ['input-files', module],
    queryFn: () => apiClient.files.listInput(module),
    staleTime: 2 * 60 * 1000, // 2 minutes
    retry: 3,
  });
}

export function useRawFileContent(filepath: string | null): UseQueryResult<RawFileViewResponse, Error> {
  const apiClient = useAPIClient();

  return useQuery({
    queryKey: ['raw-file-content', filepath],
    queryFn: () => apiClient.files.viewRaw(filepath!),
    enabled: !!filepath,
    staleTime: 10 * 60 * 1000, // 10 minutes (files don't change often)
    retry: 2,
  });
}

export function useProcessedFileContent(filename: string | null): UseQueryResult<ProcessedFileResponse, Error> {
  const apiClient = useAPIClient();

  return useQuery({
    queryKey: ['processed-file-content', filename],
    queryFn: () => apiClient.files.getProcessed(filename!),
    enabled: !!filename,
    staleTime: 10 * 60 * 1000, // 10 minutes
    retry: 2,
  });
}

export function useFileMapping(inputFilepath: string | null): UseQueryResult<FileMappingResponse, Error> {
  const apiClient = useAPIClient();

  return useQuery({
    queryKey: ['file-mapping', inputFilepath],
    queryFn: () => apiClient.files.getMapping(inputFilepath!),
    enabled: !!inputFilepath,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
  });
}

// Helper hook to download raw files
export function useDownloadRawFile() {
  const apiClient = useAPIClient();
  
  return async (filepath: string, filename: string) => {
    try {
      const response = await apiClient.files.downloadRaw(filepath);
      
      if (!response.ok) {
        throw new Error('Download failed');
      }
      
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      return true;
    } catch (error) {
      console.error('Download failed:', error);
      return false;
    }
  };
}